import { useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { formatPaise } from "@/lib/format/money";
import { CHECKOUT_BLOCKER_TEXT } from "@/lib/health-products/pricing";

export const Route = createFileRoute("/_admin/admin/product-orders")({
  head: () => ({ meta: [{ title: "Health Product Orders · Admin" }, { name: "robots", content: "noindex" }] }),
  component: ProductOrdersPage,
});

type Settings = {
  checkout_enabled: boolean;
  shipping_model: "free" | "flat" | null;
  flat_shipping_paise: number | null;
  free_shipping_min_paise: number | null;
  delivery_scope: "all_india" | "pincodes" | null;
  allowed_pincodes: string[];
  razorpay_enabled: boolean;
  razorpay_mode: "test" | "live";
  cash_enabled: boolean;
  cash_min_order_paise: number | null;
  cheque_enabled: boolean;
  notifications_enabled: boolean;
};
const EMPTY: Settings = {
  checkout_enabled: false, shipping_model: null, flat_shipping_paise: null, free_shipping_min_paise: null,
  delivery_scope: null, allowed_pincodes: [], razorpay_enabled: false, razorpay_mode: "test",
  cash_enabled: false, cash_min_order_paise: null, cheque_enabled: false, notifications_enabled: false,
};

const PAY_LABEL: Record<string, string> = {
  awaiting_payment: "Awaiting online payment", paid: "Paid", failed: "Online payment failed", cash_due: "Cash due",
  cheque_pending: "Cheque pending", cheque_failed: "Cheque failed", cancelled: "Cancelled",
};
const ACTION_ERR: Record<string, string> = {
  invalid_transition: "That change isn't allowed from the order's current status.",
  payment_not_settled: "Payment must be paid (or cash due) before fulfilment.",
  paid_order_needs_refund: "Paid orders can't be cancelled here; refunds aren't supported for product orders yet.",
  forbidden: "You don't have permission.",
};

const rupeesToPaise = (v: string) => (v.trim() === "" ? null : Math.round(Number(v) * 100));
const paiseToRupees = (v: number | null) => (v == null ? "" : String(v / 100));

function ProductOrdersPage() {
  return (
    <div className="space-y-8">
      <AdminPageHeader title="Health Product Orders" description="Orders, payments and checkout settings for the Health Products shop. Separate from food orders." />
      <SettingsCard />
      <OrdersCard />
    </div>
  );
}

function SettingsCard() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["admin", "product-order-settings"],
    queryFn: async () => {
      const { data: tenant } = await supabase.rpc("current_tenant_id");
      const { data, error } = await supabase.from("product_order_settings").select("*").eq("tenant_id", tenant as string).maybeSingle();
      if (error) throw error;
      const status = await supabase.rpc("product_checkout_status");
      return { tenant: tenant as string, settings: (data as Settings | null) ?? EMPTY, blocker: (status.data as { blocker: string | null } | null)?.blocker ?? null };
    },
  });
  const [s, setS] = useState<Settings>(EMPTY);
  const [pins, setPins] = useState("");
  useEffect(() => {
    if (q.data) {
      setS(q.data.settings);
      setPins(q.data.settings.allowed_pincodes.join(", "));
    }
  }, [q.data]);

  const save = useMutation({
    mutationFn: async () => {
      const allowed = pins.split(/[\s,]+/).map((p) => p.trim()).filter(Boolean);
      if (allowed.some((p) => !/^[1-9]\d{5}$/.test(p))) throw new Error("Pincodes must be 6 digits.");
      const { error } = await supabase.from("product_order_settings").upsert({
        ...s, allowed_pincodes: allowed, tenant_id: q.data!.tenant, updated_at: new Date().toISOString(),
      });
      if (error) throw error;
    },
    onSuccess: () => { toast.success("Settings saved"); qc.invalidateQueries({ queryKey: ["admin", "product-order-settings"] }); },
    onError: (e) => toast.error((e as Error).message || "Could not save settings"),
  });

  const up = <K extends keyof Settings>(k: K, v: Settings[K]) => setS((x) => ({ ...x, [k]: v }));
  if (q.isLoading) return <p className="text-sm text-muted-foreground">Loading settings…</p>;
  if (q.isError) return <Alert variant="destructive"><AlertDescription>Could not load settings.</AlertDescription></Alert>;

  return (
    <section className="space-y-4 rounded-xl border border-border bg-card p-5">
      <h2 className="text-lg font-semibold">Checkout settings</h2>
      {q.data?.blocker ? (
        <Alert><AlertDescription>Checkout is blocked: {CHECKOUT_BLOCKER_TEXT[q.data.blocker] ?? q.data.blocker} Customers cannot order until this is fixed.</AlertDescription></Alert>
      ) : (
        <Alert><AlertDescription>Checkout is open for eligible products.</AlertDescription></Alert>
      )}
      <div className="grid gap-4 md:grid-cols-2">
        <Toggle label="Open checkout to customers" checked={s.checkout_enabled} onChange={(v) => up("checkout_enabled", v)} />
        <div className="space-y-1">
          <Label>Shipping model</Label>
          <select className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={s.shipping_model ?? ""} onChange={(e) => up("shipping_model", (e.target.value || null) as Settings["shipping_model"])}>
            <option value="">Not set</option><option value="free">Free shipping</option><option value="flat">Flat fee</option>
          </select>
        </div>
        {s.shipping_model === "flat" ? (
          <>
            <Num label="Flat shipping fee (₹)" value={paiseToRupees(s.flat_shipping_paise)} onChange={(v) => up("flat_shipping_paise", rupeesToPaise(v))} />
            <Num label="Free shipping from order value (₹, optional)" value={paiseToRupees(s.free_shipping_min_paise)} onChange={(v) => up("free_shipping_min_paise", rupeesToPaise(v))} />
          </>
        ) : null}
        <div className="space-y-1">
          <Label>Delivery area</Label>
          <select className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={s.delivery_scope ?? ""} onChange={(e) => up("delivery_scope", (e.target.value || null) as Settings["delivery_scope"])}>
            <option value="">Not set</option><option value="all_india">All India</option><option value="pincodes">Only listed pincodes</option>
          </select>
        </div>
        {s.delivery_scope === "pincodes" ? (
          <div className="space-y-1 md:col-span-2"><Label>Allowed pincodes (comma separated)</Label><Input value={pins} onChange={(e) => setPins(e.target.value)} /></div>
        ) : null}
        <Toggle label="Razorpay online payment" checked={s.razorpay_enabled} onChange={(v) => up("razorpay_enabled", v)} />
        <div className="space-y-1">
          <Label>Razorpay mode</Label>
          <select className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={s.razorpay_mode} onChange={(e) => up("razorpay_mode", e.target.value as "test" | "live")}>
            <option value="test">Test (no real money)</option><option value="live">Live</option>
          </select>
        </div>
        <Toggle label="Cash payment" checked={s.cash_enabled} onChange={(v) => up("cash_enabled", v)} />
        {s.cash_enabled ? <Num label="Cash minimum order (₹, optional)" value={paiseToRupees(s.cash_min_order_paise)} onChange={(v) => up("cash_min_order_paise", rupeesToPaise(v))} /> : <div />}
        <Toggle label="Cheque payment" checked={s.cheque_enabled} onChange={(v) => up("cheque_enabled", v)} />
        <Toggle label="Product-order emails (not yet available — stays off)" checked={false} onChange={() => undefined} disabled />
      </div>
      <Button onClick={() => save.mutate()} disabled={save.isPending}>Save settings</Button>
    </section>
  );
}

function Toggle({ label, checked, onChange, disabled }: { label: string; checked: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
  return <label className="flex items-center justify-between gap-3 rounded-md border border-border p-3 text-sm">{label}<Switch checked={checked} onCheckedChange={onChange} disabled={disabled} /></label>;
}
function Num({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return <div className="space-y-1"><Label>{label}</Label><Input type="number" min={0} step="0.01" value={value} onChange={(e) => onChange(e.target.value)} /></div>;
}

type OrderRow = {
  id: string; order_number: string; created_at: string; customer_name: string; customer_phone: string; customer_email: string | null;
  shipping_address: Record<string, string | null>; items_total_paise: number; gst_total_paise: number; shipping_paise: number; total_paise: number;
  payment_method: string; payment_status: string; fulfilment_status: string; cheque_reference: string | null; paid_at: string | null; payment_environment: string | null;
};

function OrdersCard() {
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState<OrderRow | null>(null);
  const q = useQuery({
    queryKey: ["admin", "product-orders", filter],
    queryFn: async () => {
      let req = supabase.from("product_orders").select("*").order("created_at", { ascending: false }).limit(200);
      if (filter !== "all") req = req.eq("payment_status", filter);
      const { data, error } = await req;
      if (error) throw error;
      return data as unknown as OrderRow[];
    },
  });
  return (
    <section className="space-y-4 rounded-xl border border-border bg-card p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold">Orders</h2>
        <select aria-label="Filter by payment status" className="rounded-md border border-input bg-background px-3 py-2 text-sm" value={filter} onChange={(e) => setFilter(e.target.value)}>
          <option value="all">All</option>
          {Object.entries(PAY_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>
      {q.isLoading ? <p className="text-sm text-muted-foreground">Loading…</p> : q.isError ? (
        <Alert variant="destructive"><AlertDescription>Could not load orders.</AlertDescription></Alert>
      ) : (q.data ?? []).length === 0 ? <p className="text-sm text-muted-foreground">No health product orders yet.</p> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-muted-foreground"><tr><th className="p-2">Order</th><th className="p-2">Date</th><th className="p-2">Customer</th><th className="p-2">Total</th><th className="p-2">Payment</th><th className="p-2">Fulfilment</th></tr></thead>
            <tbody>
              {q.data!.map((o) => (
                <tr key={o.id} className="cursor-pointer border-t border-border hover:bg-muted/50" onClick={() => setOpen(o)}>
                  <td className="p-2 font-medium">{o.order_number}{o.payment_environment === "test" ? <Badge variant="outline" className="ml-2">Test</Badge> : null}</td>
                  <td className="p-2">{new Date(o.created_at).toLocaleString("en-IN")}</td>
                  <td className="p-2">{o.customer_name}<div className="text-xs text-muted-foreground">{o.customer_phone}</div></td>
                  <td className="p-2">{formatPaise(o.total_paise)}</td>
                  <td className="p-2">{o.payment_method} · {PAY_LABEL[o.payment_status] ?? o.payment_status}</td>
                  <td className="p-2 capitalize">{o.fulfilment_status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <OrderDialog order={open} onClose={() => setOpen(null)} />
    </section>
  );
}

function OrderDialog({ order, onClose }: { order: OrderRow | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [ref, setRef] = useState("");
  const detail = useQuery({
    enabled: !!order,
    queryKey: ["admin", "product-order", order?.id],
    queryFn: async () => {
      const [lines, events, fresh] = await Promise.all([
        supabase.from("product_order_lines").select("*").eq("order_id", order!.id),
        supabase.from("product_order_events").select("*").eq("order_id", order!.id).order("created_at"),
        supabase.from("product_orders").select("*").eq("id", order!.id).single(),
      ]);
      if (lines.error || events.error || fresh.error) throw new Error("load_failed");
      return { lines: lines.data, events: events.data, order: fresh.data as unknown as OrderRow };
    },
  });
  const act = useMutation({
    mutationFn: async (action: string) => {
      const { error } = await supabase.rpc("admin_product_order_action", { _order_id: order!.id, _action: action, _reference: ref || undefined });
      if (error) {
        const key = Object.keys(ACTION_ERR).find((k) => error.message.includes(k));
        throw new Error(key ? ACTION_ERR[key] : "Could not update the order.");
      }
    },
    onSuccess: () => { toast.success("Order updated"); qc.invalidateQueries({ queryKey: ["admin", "product-order"] }); qc.invalidateQueries({ queryKey: ["admin", "product-orders"] }); },
    onError: (e) => toast.error((e as Error).message),
  });
  const o = detail.data?.order ?? order;
  const confirmAct = (action: string, text: string) => { if (window.confirm(text)) act.mutate(action); };

  return (
    <Dialog open={!!order} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader><DialogTitle>Order {o?.order_number}</DialogTitle></DialogHeader>
        {o ? (
          <div className="space-y-4 text-sm">
            <div className="grid gap-2 sm:grid-cols-2">
              <div><p className="font-medium">{o.customer_name}</p><p>{o.customer_phone}</p>{o.customer_email ? <p>{o.customer_email}</p> : null}</div>
              <div className="text-muted-foreground">{[o.shipping_address.line1, o.shipping_address.line2, o.shipping_address.city, o.shipping_address.state, o.shipping_address.pincode].filter(Boolean).join(", ")}</div>
            </div>
            <table className="w-full">
              <thead className="text-left text-muted-foreground"><tr><th>Product</th><th>Qty</th><th>Unit</th><th>GST</th><th className="text-right">Total</th></tr></thead>
              <tbody>
                {(detail.data?.lines ?? []).map((l) => (
                  <tr key={l.id} className="border-t border-border"><td>{l.name}<div className="text-xs text-muted-foreground">SKU {l.sku}</div></td><td>{l.quantity}</td><td>{formatPaise(l.unit_price_paise)}</td><td>{Number(l.gst_rate_percent)}% {l.price_includes_gst ? "incl." : "added"} · {formatPaise(l.gst_paise)}</td><td className="text-right">{formatPaise(l.line_total_paise)}</td></tr>
                ))}
              </tbody>
            </table>
            <dl className="ml-auto w-60 space-y-1">
              <div className="flex justify-between"><dt>Items</dt><dd>{formatPaise(o.items_total_paise)}</dd></div>
              <div className="flex justify-between text-muted-foreground"><dt>of which GST</dt><dd>{formatPaise(o.gst_total_paise)}</dd></div>
              <div className="flex justify-between"><dt>Shipping</dt><dd>{formatPaise(o.shipping_paise)}</dd></div>
              <div className="flex justify-between font-semibold"><dt>Total</dt><dd>{formatPaise(o.total_paise)}</dd></div>
            </dl>
            <p>Payment: <strong>{o.payment_method}</strong> · {PAY_LABEL[o.payment_status] ?? o.payment_status}{o.cheque_reference ? ` · Cheque ${o.cheque_reference}` : ""}</p>
            <p>Fulfilment: <strong className="capitalize">{o.fulfilment_status}</strong></p>
            <div className="flex flex-wrap gap-2">
              {o.payment_status === "cash_due" ? <Button size="sm" onClick={() => confirmAct("cash_collected", "Confirm the full cash amount has been collected?")}>Mark cash collected</Button> : null}
              {o.payment_status === "cheque_pending" ? (
                <>
                  <Input className="w-48" placeholder="Cheque number" value={ref} onChange={(e) => setRef(e.target.value)} aria-label="Cheque number" />
                  <Button size="sm" onClick={() => confirmAct("cheque_cleared", "Confirm the cheque has cleared in the bank?")}>Mark cheque cleared</Button>
                  <Button size="sm" variant="outline" onClick={() => confirmAct("cheque_failed", "Mark this cheque as failed/bounced?")}>Cheque failed</Button>
                </>
              ) : null}
              {o.fulfilment_status === "new" && ["paid", "cash_due"].includes(o.payment_status) ? <Button size="sm" variant="outline" onClick={() => act.mutate("processing")}>Mark processing</Button> : null}
              {["new", "processing"].includes(o.fulfilment_status) && ["paid", "cash_due"].includes(o.payment_status) ? <Button size="sm" variant="outline" onClick={() => act.mutate("shipped")}>Mark shipped</Button> : null}
              {o.fulfilment_status === "shipped" ? <Button size="sm" variant="outline" onClick={() => act.mutate("delivered")}>Mark delivered</Button> : null}
              {o.payment_status !== "paid" && !["shipped", "delivered", "cancelled"].includes(o.fulfilment_status) ? (
                <Button size="sm" variant="destructive" onClick={() => confirmAct("cancel", "Cancel this order? Stock will be returned.")}>Cancel order</Button>
              ) : null}
            </div>
            <div>
              <p className="mb-1 font-medium">History</p>
              <ul className="space-y-1 text-xs text-muted-foreground">
                {(detail.data?.events ?? []).map((e) => <li key={e.id}>{new Date(e.created_at).toLocaleString("en-IN")} — {e.event}{e.to_status ? ` → ${e.to_status}` : ""}{e.note ? ` (${e.note})` : ""}</li>)}
              </ul>
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
